function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo, useState } from 'react';
import compact from 'lodash/compact';
import ServicePanel from "../ServicePanel/ServicePanel";
import { invoiceDraftModel, serviceModel } from "../../models";
import { Callout, DropdownMenu, DropdownMenuItem, IconButton, Spinner } from 'seagull';
import { Edit2Liner24, MoreVertical, Void } from 'seagull/icons';
import { useServiceAdvancedSettingsAdditionalSection } from "../ServiceAdvancedSettings";
import { changeServicePriceAndDiscount, getAmendedNextBillingDateText, getServiceAutoApprovalDate } from "./EditAgreementModal.utils";
import { BillingDayOfMonthModel } from "../../models";
import { getServiceChangeBullets } from "../../business-logic/amendmentsLogic";
import AmendmentChanges from "../AmendmentChanges/AmendmentChanges";
import { useGlobalContext, useIoContext, useStoreActions } from "../../contexts";
import { handleRequest } from "../../infra/utils";
import isEqual from 'lodash/isEqual';
import ServiceBuilder from "../ServiceCreation/ServiceBuilder/ServiceBuilder";
import TerminateServiceModal from "../TerminateServiceModal";
const EditAgreementService = /*#__PURE__*/React.forwardRef(function EditAgreementService(props, ref) {
  const {
    service,
    relationship,
    agreementData,
    idx,
    showServiceValidationWarning,
    originalService,
    onServiceChange,
    onRemoveService
  } = props;
  const testPrefix = "service-".concat(idx);
  const {
    getNow
  } = useIoContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const [amendedNextBillingDate, setAmendedNextBillingDate] = useState();
  const [loadingAmendedNextBillingDate, setLoadingAmendedNextBillingDate] = useState(false);
  const isUnsaved = !isEqual(originalService, service);
  const isFreshlyAdded = !originalService;
  const disableStatusBadge = serviceModel.isAmended(service.status) && !!originalService && !serviceModel.isAmended(originalService.status);
  const changeBullets = useMemo(() => {
    return getServiceChangeBullets(service);
  }, [service]);
  const autoApprovalDate = useMemo(() => {
    return getServiceAutoApprovalDate(service, relationship, getNow());
  }, [service, relationship]);
  const serviceAdvancedSettingsAdditionalSection = useServiceAdvancedSettingsAdditionalSection({
    relationshipState: {
      type: 'approved'
    },
    service: serviceModel.applyChanges(service),
    testPrefix,
    showServiceValidationWarning,
    onPausedChange,
    onBillOnAcceptanceChange,
    onProratedChange,
    onLimitBillingTimesChange,
    onAutoPriceIncreaseChange,
    onQboClassChange
  });
  async function onEditService() {
    logBI(11005, {
      name: service.name,
      id: service.id
    });
    const editedService = await globalContext.showModal({
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/React.createElement(ServiceBuilder, _extends({
        applyOutputToChanges: !serviceModel.isAwaitingApproval(service.status),
        service: serviceModel.applyChanges(service),
        serviceOrigin: "agreement",
        agreementData: agreementData
      }, p))
    });
    if (!editedService) return;
    if (!serviceModel.isAwaitingApproval(service.status)) {
      const withChanges = serviceModel.applyChanges(editedService);
      logBI(11011, {
        id: withChanges.id,
        name: withChanges.name
      });
      const newChanges = serviceModel.changes(editedService);
      if (newChanges) {
        onServiceChange(serviceModel.setChanges(service, newChanges));
      }
    } else {
      logBI(11011, {
        id: editedService.id,
        name: editedService.name
      });
      onServiceChange(editedService);
    }
  }
  function onStopService() {
    globalContext.showModal({
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(TerminateServiceModal, {
          serviceName: service.name,
          agreementId: relationship.id,
          stoppageMayAffectNextBill: serviceModel.isOneTimeManual(service.billing) || serviceModel.isRepeatableManual(service.billing) || serviceModel.isOneTimeDelivery(service.billing),
          canPauseService: serviceModel.isOngoing(service.billing),
          onKeepService: () => {
            dismissModal();
            onServiceChange(serviceModel.setTerminated(service));
          },
          onStopService: dismissModal
        });
      }
    });
  }
  function onProratedChange(serviceId, prorated) {
    onServiceChange(serviceModel.setProrated(service, prorated));
  }
  function onNoteChange(note) {
    onServiceChange(serviceModel.setNote(service, note));
  }
  function onPausedChange(serviceId, paused) {
    onServiceChange(serviceModel.setPaused(service, paused));
  }
  function onBillOnAcceptanceChange(serviceId, billOnAcceptance) {
    onServiceChange(serviceModel.setSkipBillingOnAcceptance(service, !billOnAcceptance));
  }
  function onLimitBillingTimesChange(serviceId, enabled, limit) {
    const newService = enabled ? serviceModel.setMaxCharges(service, enabled, limit) : serviceModel.deleteMaxCharges(service);
    onServiceChange(newService);
  }
  function onAutoPriceIncreaseChange(serviceId, priceIncrease) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setPriceIncrease(service, priceIncrease) : serviceModel.setPriceIncreaseChanges(service, priceIncrease);
    onServiceChange(newService);
  }
  function onBillUpfrontModeChange(serviceId, billUpfront) {
    onServiceChange(serviceModel.setBillUpfront(service, billUpfront));
  }
  function onDayOfMonthChange(serviceId, dayOfMonth) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setBillingDayOfMonth(service, dayOfMonth) : serviceModel.setBillingDayOfMonthChanges(service, dayOfMonth);
    onServiceChange(newService);
    if (originalService && !BillingDayOfMonthModel.isEqual(serviceModel.billingDayOfMonth(serviceModel.applyChanges(originalService)), serviceModel.billingDayOfMonth(serviceModel.applyChanges(newService)))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onDayOfWeekChange(serviceId, dayOfWeek) {
    const newService = serviceModel.isAwaitingApproval(service.status) ? serviceModel.setBillingDayOfWeek(service, dayOfWeek) : serviceModel.setBillingDayOfWeekChanges(service, dayOfWeek);
    onServiceChange(newService);
    if (originalService && serviceModel.billingDayOfWeek(serviceModel.applyChanges(originalService)) !== serviceModel.billingDayOfWeek(serviceModel.applyChanges(newService))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onQboClassChange(serviceId, integratedClass) {
    const newService = serviceModel.setIntegratedClass(service, integratedClass);
    onServiceChange(newService);
  }
  function onDiscardChanges() {
    if (serviceModel.isAwaitingApproval(service.status)) {
      if (originalService) {
        logBI(11007, {
          id: service.id,
          name: service.name
        });
        onServiceChange(serviceModel.setCanceled(service));
      } else {
        onRemoveService(service.id);
      }
    } else {
      logBI(11012, {
        id: service.id,
        name: service.name
      });
      onServiceChange(serviceModel.discardChanges(service));
    }
    clearAmendedNextBillingDate();
  }
  async function fetchAndSetAmendedNextBillingDate(service) {
    setLoadingAmendedNextBillingDate(true);
    const result = await handleRequest(api.invoiceDraft.getForAmendedService(relationship.id, service));
    if (!result.success) {
      clearAmendedNextBillingDate();
    } else {
      const {
        issueDate,
        lineItem
      } = result.data;
      const isOverlappingPeriod = invoiceDraftModel.isOverlappingPeriod(lineItem);
      if (isOverlappingPeriod) {
        setAmendedNextBillingDate(issueDate);
      } else {
        clearAmendedNextBillingDate();
      }
    }
    setLoadingAmendedNextBillingDate(false);
  }
  function clearAmendedNextBillingDate() {
    if (amendedNextBillingDate) {
      setAmendedNextBillingDate(null);
    }
  }
  return /*#__PURE__*/React.createElement(ServicePanel, {
    useBadges: true,
    isVendor: true,
    service: service,
    idx: idx,
    ref: ref,
    error: showServiceValidationWarning,
    disableStatusBadge: disableStatusBadge,
    agreementData: agreementData,
    agreementStatus: relationship.status,
    disableEditBillUpfront: !isFreshlyAdded,
    disableEditBillingDay: !isFreshlyAdded && serviceModel.isRepeatableManual(service.billing),
    onDayOfMonthChange: onDayOfMonthChange,
    onDayOfWeekChange: onDayOfWeekChange,
    onBillUpfrontModeChange: onBillUpfrontModeChange,
    onCostChange: cost => onServiceChange(changeServicePriceAndDiscount(service, cost)),
    additionalSections: compact([serviceModel.isAwaitingApproval(service.status) && serviceModel.isOnApproval(service.billing) && isUnsaved && {
      disableBorder: true,
      value: /*#__PURE__*/React.createElement(Callout, {
        "data-testid": "".concat(testPrefix, "-onApproval-service-info")
      }, "Since this is a one-time, automatically billed service, an invoice will be issued once amendments are saved and payment will require client approval.")
    }, amendedNextBillingDate && {
      disableBorder: true,
      value: /*#__PURE__*/React.createElement(Callout, {
        intent: "warning",
        "data-testid": "".concat(testPrefix, "-amended-next-billing-date")
      }, loadingAmendedNextBillingDate ? /*#__PURE__*/React.createElement(Spinner, {
        centeredInBox: true,
        centerBoxHeight: 30,
        size: 30,
        "data-testid": "".concat(testPrefix, "-amended-next-billing-date-loading")
      }) : getAmendedNextBillingDateText(service, amendedNextBillingDate))
    }, serviceAdvancedSettingsAdditionalSection]),
    footer: changeBullets.length > 0 && /*#__PURE__*/React.createElement(AmendmentChanges, {
      testid: "".concat(testPrefix, "-changes"),
      items: changeBullets,
      existingNote: serviceModel.note(service),
      isUnsaved: isUnsaved,
      autoApprovalDate: autoApprovalDate,
      onDiscard: onDiscardChanges,
      onNoteChange: onNoteChange
    }),
    menu: !(serviceModel.isCompleted(service.status) || serviceModel.isTerminated(service.status)) && /*#__PURE__*/React.createElement(DropdownMenu, {
      target: /*#__PURE__*/React.createElement(IconButton, {
        type: "button",
        "data-testid": "".concat(testPrefix, "-menu")
      }, /*#__PURE__*/React.createElement(MoreVertical, null))
    }, /*#__PURE__*/React.createElement(DropdownMenuItem, {
      iconStart: /*#__PURE__*/React.createElement(Edit2Liner24, null),
      "data-testid": "".concat(testPrefix, "-edit"),
      onClick: onEditService
    }, "Edit"), (serviceModel.isAmended(service.status) || serviceModel.isApproved(service.status)) && /*#__PURE__*/React.createElement(DropdownMenuItem, {
      iconStart: /*#__PURE__*/React.createElement(Void, null),
      "data-testid": "".concat(testPrefix, "-stop"),
      onClick: onStopService
    }, "Stop"))
  });
});
export default EditAgreementService;